import { formatCurrency } from '@angular/common';
import { BenefitsAmounts, BenefitsCheck, BenefitsCheckStatus } from 'src/app/models/patient-enrollment.model';

/** Colors reuse the Material chip palette the Priority and Status columns already use. */
export type BenefitsBadgeColor = 'primary' | 'accent' | 'warn' | 'default';

export interface BenefitsBadge {
  /** Visible text: the meaning never rests on color alone. */
  label: string;
  color: BenefitsBadgeColor;
};

const STATUS_BADGES: Record<BenefitsCheckStatus, BenefitsBadge> = {
  pending: { label: 'Pending', color: 'default' },
  dry_run: { label: 'Practice', color: 'default' },
  eligible: { label: 'Eligible', color: 'primary' },
  ineligible: { label: 'Ineligible', color: 'warn' },
  needs_review: { label: 'Needs review', color: 'accent' },
  deferred: { label: 'Retry later', color: 'default' },
  checked_recently: { label: 'Checked recently', color: 'default' },
  error: { label: 'Error', color: 'warn' },
  traditional_medicare_manual: { label: 'Medicare (manual)', color: 'accent' },
};

export const INSURANCE_MISMATCH_LABEL = 'Insurance mismatch';

/** null when the row has no check or carries a status this build does not know (shows nothing). */
export const benefitsBadge = (check: BenefitsCheck | null | undefined): BenefitsBadge | null => (check && STATUS_BADGES[check.status]) || null;

export const hasInsuranceMismatch = (check: BenefitsCheck | null | undefined): boolean => !!check?.flags?.includes('insurance_mismatch');

/** What the "Needs review" filter keeps. */
export const benefitsNeedsReview = (check: BenefitsCheck | null | undefined): boolean =>
  !!check && (check.needsReview === true || check.status === 'needs_review' || hasInsuranceMismatch(check));

/** Tooltip and screen-reader text: the backend's PHI-free reason plus when it was checked. */
export const benefitsTooltip = (check: BenefitsCheck | null | undefined): string => {
  if (!check) {
    return '';
  }
  const parts: string[] = [];
  if (check.reasonText) {
    parts.push(check.reasonText);
  }
  const checkedAt = check.checkedAt ? new Date(check.checkedAt) : null;
  if (checkedAt && !isNaN(checkedAt.getTime())) {
    parts.push(`Checked ${checkedAt.toLocaleString()}`);
  }
  return parts.join(' · ');
};

/** Amounts older than this are shown as stale. */
export const BENEFITS_AMOUNTS_STALE_AFTER_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

export interface BenefitsAmountsDisplay {
  /** Compact one-line text for the row, e.g. "Ded $500 left of $1,500 · OOP $2,000 left · Copay $25". */
  summary: string;
  /** Full set, one line each; used for the tooltip and screen readers. */
  details: string[];
  /** "as of <date>", or null when the date is missing or unreadable. */
  asOf: string | null;
  stale: boolean;
  /** Visible text that explains why the amounts are stale; null when fresh. */
  staleLabel: string | null;
}

/** Only real numbers count: a missing or garbled field is unknown and is never rendered as $0. */
export const num = (value: unknown): number | undefined => (typeof value === 'number' && isFinite(value) ? value : undefined);

/** USD, no cents when whole. */
export const formatUsd = (value: number): string => formatCurrency(value, 'en-US', '$', 'USD', Number.isInteger(value) ? '1.0-0' : '1.2-2');

/** Whole percent, e.g. 20 -> "20%". */
export const formatPercent = (value: number): string => `${Number(value.toFixed(2))}%`;

const validDate = (iso: string | undefined): Date | null => {
  const date = iso ? new Date(iso) : null;
  return date && !isNaN(date.getTime()) ? date : null;
};

export const remainingOfTotal = (remaining: number | undefined, total: number | undefined): string | null => {
  if (remaining !== undefined && total !== undefined) {
    return `${formatUsd(remaining)} left of ${formatUsd(total)}`;
  }
  if (remaining !== undefined) {
    return `${formatUsd(remaining)} left`;
  }
  return total !== undefined ? `${formatUsd(total)} total` : null;
};

const amountLines = (b: BenefitsAmounts): string[] => {
  const lines: Array<[string, string | null]> = [
    ['Deductible (individual)', remainingOfTotal(num(b.individualDeductibleRemaining), num(b.individualDeductibleTotal))],
    ['Out-of-pocket max (individual)', remainingOfTotal(num(b.individualOopRemaining), num(b.individualOopTotal))],
    ['Deductible (family)', remainingOfTotal(num(b.familyDeductibleRemaining), num(b.familyDeductibleTotal))],
    ['Out-of-pocket max (family)', remainingOfTotal(num(b.familyOopRemaining), num(b.familyOopTotal))],
    ['Office visit copay', num(b.officeVisitCopay) !== undefined ? formatUsd(b.officeVisitCopay as number) : null],
    ['Specialist copay', num(b.specialistCopay) !== undefined ? formatUsd(b.specialistCopay as number) : null],
    ['Office visit coinsurance', num(b.officeVisitCoinsurance) !== undefined ? formatPercent(b.officeVisitCoinsurance as number) : null],
    ['Specialist coinsurance', num(b.specialistCoinsurance) !== undefined ? formatPercent(b.specialistCoinsurance as number) : null],
  ];
  return lines.filter(([, value]) => value !== null).map(([label, value]) => `${label}: ${value}`);
};

const summaryParts = (b: BenefitsAmounts): string[] => {
  const parts: string[] = [];
  const ded = remainingOfTotal(num(b.individualDeductibleRemaining), num(b.individualDeductibleTotal));
  // Individual first; the family deductible takes its place only when the payer sent no individual one.
  const familyDed = ded ? null : remainingOfTotal(num(b.familyDeductibleRemaining), num(b.familyDeductibleTotal));
  if (ded) {
    parts.push(`Ded ${ded}`);
  } else if (familyDed) {
    parts.push(`Family ded ${familyDed}`);
  }
  const oopRemaining = num(b.individualOopRemaining);
  const oopTotal = num(b.individualOopTotal);
  const oop = oopRemaining !== undefined ? `${formatUsd(oopRemaining)} left` : oopTotal !== undefined ? `${formatUsd(oopTotal)} total` : null;
  if (oop) {
    parts.push(`OOP ${oop}`);
  }
  if (num(b.officeVisitCopay) !== undefined) {
    parts.push(`Copay ${formatUsd(b.officeVisitCopay as number)}`);
  }
  return parts;
};

/**
 * What the row shows next to the badge. null when there is nothing to show (no `benefits`, or none of
 * the known fields present), so the badge looks exactly as it did before amounts existed.
 * Stale (muted, with a visible label) when the backend says the coverage is no longer active, the
 * status is Traditional Medicare checked by hand (own label, not a "not covered" message), the
 * current status is not eligible, or the amounts are older than 30 days. Label precedence is in that order.
 */
export const benefitsAmountsDisplay = (check: BenefitsCheck | null | undefined, now: Date = new Date()): BenefitsAmountsDisplay | null => {
  const amounts = check?.benefits;
  if (!check || !amounts) {
    return null;
  }
  const details = amountLines(amounts);
  if (!details.length) {
    return null;
  }
  const checked = validDate(check.benefitsCheckedAt);
  const asOf = checked ? `as of ${checked.toLocaleDateString()}` : null;
  const old = !!checked && now.getTime() - checked.getTime() > BENEFITS_AMOUNTS_STALE_AFTER_DAYS * DAY_MS;

  let staleLabel: string | null = null;
  if (check.benefitsStale === true) {
    staleLabel = checked ? `Coverage not active, amounts from ${checked.toLocaleDateString()}` : 'Coverage not active, older amounts';
  } else if (check.status === 'traditional_medicare_manual') {
    // Not "not covered": the automatic check skips Traditional Medicare because a person checks it by hand.
    staleLabel = checked
      ? `Medicare: checked by hand, amounts from ${checked.toLocaleDateString()} may not apply`
      : 'Medicare: checked by hand, older amounts may not apply';
  } else if (check.status !== 'eligible') {
    staleLabel = `Stale: current status is not eligible${asOf ? `, ${asOf}` : ''}`;
  } else if (old) {
    staleLabel = `Stale: over ${BENEFITS_AMOUNTS_STALE_AFTER_DAYS} days old, ${asOf}`;
  }

  return { summary: summaryParts(amounts).join(' · '), details, asOf, stale: staleLabel !== null, staleLabel };
};

/** Tooltip and screen-reader text for the amounts. */
export const benefitsAmountsTooltip = (display: BenefitsAmountsDisplay | null): string =>
  display ? [...display.details, display.asOf ?? '', display.staleLabel ?? ''].filter(Boolean).join('\n') : '';
